/**
 * Extracts a short mono 16 kHz PCM16 WAV sample from an audio file, entirely
 * in the browser — WebAudio for what the browser can decode, ffmpeg.wasm for
 * the accepted formats it cannot (WMA). Used for language detection — the
 * backend never needs ffmpeg because it only ever receives this normalized
 * WAV.
 *
 * Returns null whenever the sample can't be produced (unsupported codec,
 * huge file, decode error) — language detection is best-effort and must
 * never block the upload flow.
 */

import { isWma } from './asf'
import { encodeWavPcm16, type PcmJob } from './pcmWav'

const TARGET_SAMPLE_RATE = 16000
const MAX_SECONDS = 30
// Frame-based formats can be decoded from just the head of the file
const HEAD_SLICE_BYTES = 2 * 1024 * 1024
// m4a/aac often keeps its sample table in a trailing moov atom, so it needs a
// full decode — only reasonable for files we can hold in memory comfortably
const FULL_DECODE_MAX_BYTES = 25 * 1024 * 1024

const SLICEABLE_EXT = /\.(mp3|wav|ogg|oga|opus|webm|flac)$/i

/**
 * Audio we accept for upload that no browser can decode.
 *
 * WMA is on the upload allowlist and WebAudio will not open it anywhere —
 * `decodeAudioData` rejects with EncodingError in Chrome, and there is no
 * browser that ships a Windows Media decoder — so for these files the
 * language-detection sample silently came back null and auto-detect never
 * ran. Measured: the model itself has no trouble with WMA audio once it is a
 * 16 kHz WAV (ru 0.99, en 0.94 on the test recordings); the only broken link
 * was the decode in the browser.
 *
 * Deliberately a list of formats, not "fall back to ffmpeg whenever WebAudio
 * fails": a corrupt mp3 would then pull the 30 MB ffmpeg core only to fail a
 * second time, and the ordinary audio path has never paid for that download.
 */
export function needsFfmpegForSample(file: Pick<File, 'name' | 'type'>): boolean {
  return isWma(file)
}

/** Render → WAV bytes, with the PCM16 loop in a worker (see pcmWav.worker.ts).
 * copyFromChannel gives us a buffer of our own to transfer — the view from
 * getChannelData() belongs to the AudioBuffer. If the worker can't run
 * (no Worker API, script failed to load) the samples are copied again and
 * encoded inline: the transferred array is detached by then. */
async function wavBlobFromRendered(rendered: AudioBuffer, sampleRate: number): Promise<Blob> {
  const samples = new Float32Array(rendered.length)
  rendered.copyFromChannel(samples, 0)
  let buffer: ArrayBuffer
  try {
    buffer = await encodeInWorker(samples, sampleRate)
  } catch {
    const again = new Float32Array(rendered.length)
    rendered.copyFromChannel(again, 0)
    buffer = encodeWavPcm16(again, sampleRate)
  }
  return new Blob([buffer], { type: 'audio/wav' })
}

function encodeInWorker(samples: Float32Array, sampleRate: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    if (typeof Worker === 'undefined') {
      reject(new Error('Web Workers unavailable'))
      return
    }
    const worker = new Worker(new URL('./pcmWav.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = (e: MessageEvent<ArrayBuffer>) => {
      worker.terminate()
      resolve(e.data)
    }
    worker.onerror = (e) => {
      worker.terminate()
      reject(e.error ?? new Error('PCM worker failed'))
    }
    const job: PcmJob = { samples, sampleRate }
    worker.postMessage(job, [samples.buffer])
  })
}

export async function extractAudioSample(file: File): Promise<Blob | null> {
  if (needsFfmpegForSample(file)) return sampleWithFfmpeg(file)
  if (typeof AudioContext === 'undefined' || typeof OfflineAudioContext === 'undefined') {
    return null
  }
  const ctx = new AudioContext()
  try {
    const decoded = await decodeHead(ctx, file)
    if (!decoded || decoded.duration < 1) return null

    const seconds = Math.min(decoded.duration, MAX_SECONDS)
    const offline = new OfflineAudioContext(1, Math.ceil(seconds * TARGET_SAMPLE_RATE), TARGET_SAMPLE_RATE)
    const source = offline.createBufferSource()
    source.buffer = decoded
    source.connect(offline.destination)
    source.start(0)
    const rendered = await offline.startRendering()
    return await wavBlobFromRendered(rendered, TARGET_SAMPLE_RATE)
  } catch {
    return null
  } finally {
    void ctx.close().catch(() => {})
  }
}

/**
 * The same sample, cut by ffmpeg.wasm — the route video already takes, with
 * the same command minus the video: 30 s rather than video's 35 so the
 * sample is the same shape the WebAudio path produces, whatever the file
 * came in as.
 *
 * Imported at the point of use, never statically: this module sits in the
 * main bundle via the upload form, and a static import would put the ffmpeg
 * wrapper in front of every landing-page visitor (see ffmpegClient.ts). The
 * file is mounted through WORKERFS rather than copied into wasm memory, and
 * the instance is torn down afterwards — nothing else on this path needs it.
 */
async function sampleWithFfmpeg(file: File): Promise<Blob | null> {
  let ffmpeg: import('./ffmpegClient').FFmpeg | null = null
  try {
    const { createFFmpeg, readOutput } = await import('./ffmpegClient')
    ffmpeg = await createFFmpeg()
    const dir = '/sample-input'
    await ffmpeg.createDir(dir)
    await ffmpeg.mount('WORKERFS' as never, { files: [file] } as never, dir)
    await ffmpeg.exec([
      '-i', `${dir}/${file.name}`,
      '-vn', '-t', String(MAX_SECONDS), '-ac', '1', '-ar', String(TARGET_SAMPLE_RATE),
      '-c:a', 'pcm_s16le', 'sample.wav',
    ])
    const data = await readOutput(ffmpeg, 'sample.wav')
    // A header and nothing else is a file with no audio in it
    if (!data || data.byteLength <= 44) return null
    return new Blob([data.slice()], { type: 'audio/wav' })
  } catch {
    return null
  } finally {
    ffmpeg?.terminate()
  }
}

async function decodeHead(ctx: AudioContext, file: File): Promise<AudioBuffer | null> {
  if (SLICEABLE_EXT.test(file.name) && file.size > HEAD_SLICE_BYTES) {
    try {
      const head = await file.slice(0, HEAD_SLICE_BYTES).arrayBuffer()
      return await ctx.decodeAudioData(head)
    } catch {
      // Truncated head not decodable in this browser — fall through
    }
  }
  if (file.size <= FULL_DECODE_MAX_BYTES) {
    try {
      return await ctx.decodeAudioData(await file.arrayBuffer())
    } catch {
      return null
    }
  }
  return null
}

/** Converts a MediaRecorder blob (webm/opus, mp4/aac — whatever the browser
 * recorded) into a mono 16 kHz PCM16 WAV file the STT gateway accepts.
 * ~1.92 MB per minute, so even a 30-minute recording stays under 60 MB. */
export async function wavFileFromRecording(blob: Blob, filename: string): Promise<File | null> {
  if (typeof AudioContext === 'undefined' || typeof OfflineAudioContext === 'undefined') {
    return null
  }
  const ctx = new AudioContext()
  try {
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer())
    if (!decoded || decoded.duration < 0.5) return null
    const offline = new OfflineAudioContext(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE), TARGET_SAMPLE_RATE)
    const source = offline.createBufferSource()
    source.buffer = decoded
    source.connect(offline.destination)
    source.start(0)
    const rendered = await offline.startRendering()
    const wav = await wavBlobFromRendered(rendered, TARGET_SAMPLE_RATE)
    return new File([wav], filename, { type: 'audio/wav' })
  } catch {
    return null
  } finally {
    void ctx.close().catch(() => {})
  }
}
